import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/common/components/modal";
import { Button } from "@/common/components/button";
import { PRODUCT_LISTING_PATH, PRODUCT_URL } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { formatToNaira } from "@/common/utils/general";
import { IProductListing } from "@/common/types/pricing";

interface ISellingLevelPriceModalProps {
  isOpen: boolean;
  close: () => void;
  productId: string;
  productName?: string;
  /** Fires after a successful save so the caller can refresh its row. */
  onSaved?: (listing: IProductListing) => void;
  /**
   * MED-698 — what the caller already knows about whether this seller's level is
   * derived, used only until the listing fetch answers definitively.
   *
   * Without it the title flickers: `listing` is null while the GET is in flight,
   * so the derived check reads false and a manufacturer is shown
   * "Set selling level & price" for as long as the request takes, before it
   * corrects itself. The inventory list already computes this for the row menu
   * label, and the menu and this modal disagreeing for a second is exactly the
   * split-brain the menu's own comment warns about.
   *
   * Optional: callers with no grid to read it from (Scan-to-Receive) omit it and
   * get the neutral title until the fetch lands.
   */
  levelIsDerivedHint?: boolean;
  /**
   * Shown at Scan-to-Receive, where the module is presented automatically and
   * must be dismissible. Skipping stores nothing and leaves the product not
   * visible for sale until it is priced from the inventory screen.
   */
  skippable?: boolean;
  /**
   * Position in a multi-product prompt queue (MED-667). Renders "Product 1 of 3"
   * so the seller can see how many are left. Omitted for a single prompt.
   */
  queuePosition?: { index: number; total: number };
  /**
   * Called when the seller is finished with THIS product — by saving or by
   * skipping. When supplied, `close` is NOT also called on those paths — it
   * then means only "dismiss" (the modal's X and backdrop), so a queueing
   * caller must own its full terminal path (e.g. advancing the queue) here.
   * When omitted, save and skip fall back to `close`, which is what a
   * single-prompt caller like Entry A wants.
   */
  onDone?: (outcome: "saved" | "skipped") => void;
}

// MED-659 (UC-LST-01) — pick the selling level and set the price in one place.
//
// The backend stores ONE canonical per-base-unit value and returns the whole
// derived ladder, so changing level here is local arithmetic against
// `listing.levels` — no round trip, and no chance of the label and the number
// disagreeing.
//
// MED-698 — for a manufacturer there is no level to pick. They sell at the
// product's highest defined layer (the carton, for a carton of packs of tablets)
// because that is what they build, so the selector is replaced by a statement of
// the derived level and only the price is collected. Every reseller below them
// breaks bulk and keeps the full picker, unchanged (MED-632).
//
// The switch is `listing.selling_level_derived`, sent by the server, NOT the
// logged-in company type: the rule is enforced server-side, so reading it from
// anywhere else risks offering a picker over an input the server will override.
// It also means this one component serves both roles at all three of its mount
// points — the inventory list, Scan-to-Receive, and Add Inventory — without any
// of them having to know which seller they are rendering for.
export function SellingLevelPriceModal({
  isOpen,
  close,
  productId,
  productName,
  onSaved,
  levelIsDerivedHint,
  skippable = false,
  queuePosition,
  onDone,
}: ISellingLevelPriceModalProps) {
  const apiCall = useApiCallHandling();

  const [listing, setListing] = useState<IProductListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [level, setLevel] = useState<number | null>(null);
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  const listingUrl = `${PRODUCT_URL}/${productId}${PRODUCT_LISTING_PATH}`;

  useEffect(() => {
    if (!isOpen || !productId) return;

    setLoading(true);
    setError("");
    apiCall(
      listingUrl,
      "GET",
      {},
      (data: IProductListing) => {
        setListing(data);
        // Current level when one exists, otherwise the outermost defined level —
        // the backend already resolves that fallback into `selling_level`.
        //
        // MED-698: where the level is derived, the ladder's first rung wins over
        // `selling_level`. The server resolves both to the same thing, so this
        // changes nothing in practice — but a stale or unexpected `selling_level`
        // on a derived listing would otherwise label the price field with a level
        // the seller is not allowed to sell at, and the field's label is what
        // tells them what the number they are typing means.
        const initialLevel = data?.selling_level_derived
          ? data?.levels?.[0]?.level ?? null
          : data?.selling_level ?? data?.levels?.[0]?.level ?? null;
        setLevel(initialLevel);
        // Pre-fill with the seller's last price re-derived to the initial level.
        // Empty on a first-ever pricing (UC-LST-01 step 5).
        const entry = data?.levels?.find((l) => l.level === initialLevel);
        setPrice(data?.priced && entry?.price ? String(Number(entry.price)) : "");
      },
      () => setError("Could not load this product's pricing."),
      () => setLoading(false),
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productId]);

  const selectedEntry = useMemo(
    () => listing?.levels?.find((l) => l.level === level) ?? null,
    [listing, level],
  );

  // MED-698 — the level is a fact about the product, not a choice. Defaults to
  // false so a server that has not been deployed yet, or a failed fetch, falls
  // back to the pre-MED-698 behaviour rather than silently hiding the selector
  // from a reseller who needs it.
  //
  // The caller's hint fills the gap while the fetch is in flight; the fetched
  // listing always wins once it arrives, because the server is the authority.
  const levelIsDerived =
    listing?.selling_level_derived ?? levelIsDerivedHint ?? false;

  // Whether the question has been ANSWERED, as opposed to answered `false`.
  // Drives the title only: a picker hidden on a guess would be a worse mistake
  // than a heading that starts unspecific, so the body still waits for `loading`.
  const levelDerivationKnown =
    listing != null || levelIsDerivedHint !== undefined;

  const baseUnitName = listing?.base_unit_packaging_type ?? "unit";

  // The live "= ₦200 per Tablet" equivalent, computed only for the preview. The
  // authoritative conversion happens server-side on save.
  const perBaseUnitPreview = useMemo(() => {
    const amount = Number(price);
    const divisor = selectedEntry?.base_units ?? 0;
    if (!price || Number.isNaN(amount) || amount <= 0 || divisor <= 0) return null;
    return amount / divisor;
  }, [price, selectedEntry]);

  // Margin against the cost captured from the platform purchase order, shown
  // only where cost is actually known. `cost_known: false` means the stock was
  // not bought through the platform — showing "0%" there would be a lie.
  const marginPreview = useMemo(() => {
    const cost = Number(listing?.margin?.cost_per_base_unit);
    if (!listing?.margin?.cost_known || !cost || !perBaseUnitPreview) return null;
    const amount = perBaseUnitPreview - cost;
    return {
      amount,
      percentage: (amount / perBaseUnitPreview) * 100,
    };
  }, [listing, perBaseUnitPreview]);

  // Re-derive the amount when the seller switches level, so the number in the
  // field always means what the label above it says.
  //
  // Both branches matter. The saved-listing branch takes the server's already
  // rounded `entry.price` rather than multiplying `price_per_base_unit` locally:
  // a canonical value like 208.333333 × 12 is 2499.999996, which would show the
  // seller a different number than the pre-fill path did for the same listing.
  //
  // The typed branch is what makes FIRST-TIME pricing correct. Previously this
  // returned early when the listing was unpriced, so the typed amount stayed put
  // while both the label and the per-base-unit preview changed under it — type
  // 24,000 at Carton, switch to Pack, and the field claimed ₦24,000 per Pack.
  function handleLevelChange(nextLevel: number) {
    const previousEntry = selectedEntry;
    setLevel(nextLevel);
    setError("");

    const nextEntry = listing?.levels?.find((l) => l.level === nextLevel);
    if (!nextEntry) return;

    // A saved price: re-read it at the new level from the server-derived ladder.
    if (listing?.priced && nextEntry.price !== null) {
      setPrice(String(Number(nextEntry.price)));
      return;
    }

    // Nothing saved yet, but something typed: carry the typed amount across by
    // converting through the base unit.
    const typed = Number(price);
    if (!price.trim() || Number.isNaN(typed) || typed <= 0) return;
    if (!previousEntry?.base_units || !nextEntry.base_units) return;

    const converted = (typed / previousEntry.base_units) * nextEntry.base_units;
    // 2dp: this is a money figure the seller reads and edits, not the canonical
    // value — the server re-derives that from whatever is finally submitted.
    setPrice(String(Number(converted.toFixed(2))));
  }

  // One exit for "done with this product". Without it, save and skip would both
  // call `close` — the same callback the modal's X and backdrop fire — and a
  // queueing caller could not tell "priced it, next please" from "let me out".
  function finish(outcome: "saved" | "skipped") {
    if (onDone) {
      onDone(outcome);
      return;
    }
    close();
  }

  function handleSave() {
    const amount = Number(price);
    if (!price.trim() || Number.isNaN(amount) || amount <= 0) {
      setError("Enter a price greater than zero.");
      return;
    }
    if (!level) {
      setError(
        levelIsDerived
          ? "This product has no packaging hierarchy, so there is no selling level to price at."
          : "Select the level you sell this product at.",
      );
      return;
    }

    setSaving(true);
    setError("");
    apiCall(
      listingUrl,
      "PUT",
      // MED-698: `selling_level` is omitted where it is derived, rather than sent
      // as the level we believe is highest. The server falls back to the derived
      // level for these sellers anyway, and a sent value still decides how the
      // AMOUNT is read — so if this client's idea of "highest" ever drifted from
      // the server's, sending it would misprice by the containment factor while
      // omitting it cannot.
      levelIsDerived ? { price } : { price, selling_level: level },
      (data: IProductListing) => {
        setListing(data);
        onSaved?.(data);
        finish("saved");
      },
      // The server is the authority on validity; surface its message rather
      // than guessing at a client-side equivalent.
      (err: any) => setError(err?.response?.data?.message || "Could not save this price."),
      () => setSaving(false),
      { dontNotifyonFail: true },
    );
  }

  const priceLabel = selectedEntry
    ? `Selling price per ${selectedEntry.packaging_type} (NGN)`
    : "Selling price (NGN)";

  // No ladder means no level can be chosen, and `handleSave` refuses without
  // one — so an enabled Save button would only ever produce "Select the level
  // you sell this product at", which the seller has no way to satisfy. This is
  // reachable whenever the listing GET fails, and MED-667 makes that far more
  // visible: Entry B opens the modal automatically rather than on a click, so a
  // failed fetch strands the seller on a dead button instead of a dead menu item.
  const hasLevels = (listing?.levels?.length ?? 0) > 0;

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      // MED-698: nothing about the level is being SET for a manufacturer, so the
      // title should not say it is. Before the answer is known it stays on the
      // shorter wording, which is true of both roles — a reseller then sees
      // "level &" appear, where a manufacturer would otherwise have seen it
      // appear and then be taken away.
      title={
        levelIsDerived || !levelDerivationKnown
          ? "Set selling price"
          : "Set selling level & price"
      }
    >
      <div className="w-[32rem] max-w-[90vw] px-4 py-2">
        {loading ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading pricing…</p>
        ) : (
          <>
            {/* Only when there is genuinely a queue — "Product 1 of 1" would be
                noise on the single-product case Entry A always is. */}
            {queuePosition && queuePosition.total > 1 && (
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                Product {queuePosition.index + 1} of {queuePosition.total}
              </p>
            )}

            {productName && (
              <p className="mb-3 text-sm font-semibold text-gray-800">{productName}</p>
            )}

            <label className="mb-1 block text-sm font-medium text-gray-700">
              {levelIsDerived ? "You sell this product by" : "I sell this product by"}
            </label>

            {/* MED-698 — a manufacturer sells at the product's highest packaging
                level, so the level is stated rather than offered. Rendered as
                text, not a disabled control: a greyed-out picker still reads as
                "there is a choice here you cannot make", which is the wrong
                message for something that was never a choice. */}
            {levelIsDerived ? (
              <div className="mb-4">
                <p className="text-sm font-semibold capitalize text-gray-800">
                  {selectedEntry?.packaging_type ?? "—"}
                  {selectedEntry && selectedEntry.base_units > 1 && (
                    <span className="ml-1 font-normal text-gray-400">
                      ({selectedEntry.base_units} {baseUnitName}
                      {selectedEntry.base_units === 1 ? "" : "s"})
                    </span>
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  This is the highest packaging level defined on the product — the level you
                  manufacture and sell at.
                </p>
              </div>
            ) : (
              <div className="mb-4 flex flex-wrap gap-2">
                {(listing?.levels ?? []).map((entry) => (
                  <button
                    key={entry.level}
                    type="button"
                    onClick={() => handleLevelChange(entry.level)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium capitalize ${
                      entry.level === level
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-600"
                    }`}
                  >
                    {entry.packaging_type}
                    {entry.base_units > 1 && (
                      <span className="ml-1 text-gray-400">
                        ({entry.base_units} {baseUnitName}
                        {entry.base_units === 1 ? "" : "s"})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="selling-price">
              {priceLabel}
            </label>
            <input
              id="selling-price"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => {
                setPrice(e.target.value);
                setError("");
              }}
              placeholder={`Type the price per ${selectedEntry?.packaging_type ?? "unit"}`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />

            <div className="mt-2 min-h-[2.5rem] text-xs">
              {perBaseUnitPreview !== null && (
                <p className="text-gray-600">
                  = {formatToNaira(Number(perBaseUnitPreview.toFixed(2)))} per {baseUnitName}
                </p>
              )}
              {marginPreview && (
                <p className={marginPreview.amount >= 0 ? "text-green-700" : "text-red-600"}>
                  Margin {formatToNaira(Number(marginPreview.amount.toFixed(2)))} per {baseUnitName} (
                  {marginPreview.percentage.toFixed(1)}%)
                </p>
              )}
              {/* Deliberately silent when cost is unknown: no cost was captured,
                  so there is no margin to state either way. */}
            </div>

            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

            <div className="mt-5 flex items-center justify-end gap-3">
              {skippable && (
                <button
                  type="button"
                  onClick={() => finish("skipped")}
                  className="text-sm font-medium text-gray-500 underline"
                >
                  Skip for now
                </button>
              )}
              <Button onClick={handleSave} disabled={saving || !hasLevels}>
                {saving ? "Saving…" : "Save price"}
              </Button>
            </div>

            {skippable && (
              <p className="mt-3 text-xs text-gray-500">
                Until a price is set, this product stays in your inventory but is not visible to
                buyers.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
